import React, { useState } from 'react';
import { Star, Plus, X, Loader2 } from 'lucide-react';

const RatingButton = ({ 
  record, 
  onRatingSubmit, 
  isSubmitting = false 
}) => {
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewData, setReviewData] = useState({
    stars: 0,
    discipline: '',
    qualityOfWork: '',
    behaviour: '',
    isEditing: false
  });

  const handleStarClick = (rating) => {
    setReviewData({
      ...reviewData,
      stars: rating,
    });
  };

  const handleInputChange = (e, field) => {
    setReviewData({
      ...reviewData,
      [field]: e.target.value,
    });
  };

  const openReviewModal = (isEditing = false) => {
    if (isEditing && record.rating) {
      // For editing, pre-fill with existing data
      setReviewData({
        stars: record.rating,
        discipline: getCommentValue(record.comments, 'Discipline'),
        qualityOfWork: getCommentValue(record.comments, 'Quality of Work'),
        behaviour: getCommentValue(record.comments, 'Behaviour'),
        isEditing: true
      });
    } else {
      // For new rating, start fresh
      setReviewData({
        stars: 0,
        discipline: '',
        qualityOfWork: '',
        behaviour: '',
        isEditing: false
      });
    }
    setShowReviewModal(true);
  };

  const closeReviewModal = () => {
    setShowReviewModal(false);
  };

  const submitReview = async () => {
    await onRatingSubmit(reviewData, record.id);
    setShowReviewModal(false);
  };

  const getCommentValue = (comments, key) => {
    if (!comments || !Array.isArray(comments)) return '';
    
    const commentObj = comments.find(c => c[key] !== undefined);
    if (commentObj) return commentObj[key];
    
    const keyValueComment = comments.find(c => c.key === key);
    return keyValueComment ? keyValueComment.value : '';
  };

  return (
    <>
      {/* Rating Display / Add Button - Keep original styling */}
      {record.rating ? (
        <div className="flex justify-center gap-0.5">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star
              key={star}
              size={16}
              onClick={(e) => {
                e.stopPropagation();
                openReviewModal(true);
              }}
              fill={star <= record.rating ? "#FFD700" : "none"}
              color={star <= record.rating ? "#FFD700" : "#D1D5DB"}
              className='cursor-pointer'
            />
          ))}
        </div>
      ) : (
        <button
          onClick={(e) => {
            e.stopPropagation();
            openReviewModal(false);
          }}
          className="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Add Rating
        </button>
      )}

      {/* Review Modal - Keep EXACT same styling as original */}
      {showReviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium mb-4">
              {reviewData.isEditing ? "Update Worker Rating" : "Add Worker Rating"}
            </h3>
            
            {record.profilePhotoUrl && (
              <div className="flex justify-center mb-4">
                <img
                  src={record.profilePhotoUrl}
                  alt="Worker Profile"
                  className="w-full h-auto rounded-lg max-h-[50vh] object-contain"
                />
              </div>
            )}
            
            <div className="flex justify-center mb-6">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  size={32}
                  className="cursor-pointer"
                  onClick={() => handleStarClick(star)}
                  fill={star <= reviewData.stars ? "#FFD700" : "none"}
                  color={star <= reviewData.stars ? "#FFD700" : "#D1D5DB"}
                />
              ))}
            </div>
            
            {reviewData.stars > 0 && reviewData.stars <= 3 && (
              <div className="mb-4 space-y-3">
                {/* EXACT same grid layout as original */}
                <div className="grid grid-cols-6 items-center">
                  <label className="col-span-2 text-sm font-medium text-gray-700">
                    Discipline:
                  </label>
                  <input
                    type="text"
                    value={reviewData.discipline}
                    onChange={(e) => handleInputChange(e, 'discipline')}
                    className="col-span-4 px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Enter discipline feedback"
                  />
                </div>
                
                <div className="grid grid-cols-6 items-center">
                  <label className="col-span-2 text-sm font-medium text-gray-700">
                    Quality of Work:
                  </label>
                  <input
                    type="text"
                    value={reviewData.qualityOfWork}
                    onChange={(e) => handleInputChange(e, 'qualityOfWork')}
                    className="col-span-4 px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Enter quality feedback"
                  />
                </div>
                
                <div className="grid grid-cols-6 items-center">
                  <label className="col-span-2 text-sm font-medium text-gray-700">
                    Behaviour:
                  </label>
                  <input
                    type="text"
                    value={reviewData.behaviour}
                    onChange={(e) => handleInputChange(e, 'behaviour')}
                    className="col-span-4 px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Enter behaviour feedback"
                  />
                </div>
              </div>
            )}
            
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowReviewModal(false)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={submitReview}
                disabled={reviewData.stars === 0 || (reviewData.stars <= 3 && !reviewData.discipline && !reviewData.qualityOfWork && !reviewData.behaviour)}
                className="px-4 py-2 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed"
              >
                {reviewData.isEditing ? "Update" : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RatingButton;